import {
  allowCreatingEntities,
  allowDeletingEntities,
  allowReadingEntities,
  allowUpdatingAtoms,
  allowUpdatingEntities,
  DefaultAtoms,
  DefaultEntities,
  GenericPermission,
  grantFieldPermission,
  inviteUserPermission,
  makePermission,
  type PermissionsByRole,
  requestEmailChangePermission,
  resendInviteCodePermission,
  sendPasswordResetEmailPermission,
  type ValidProjectSpecificPermissions,
} from "@atoms/atom-types";
import { Atoms } from "../enums/atoms";
import { Entities } from "../enums/entities";
import { CustomPermissions } from "../generated/custom-permissions";
import {
  afsImportApiPermission,
  clearAllPresetsPermission,
  clearAllPriceDataPermission,
  clearAllUserOptionsPermission,
  deleteAllPositionPreviewsPermission,
  deleteModelAssetPermission,
  deletePositionPreviewPermission,
  getUploadModelAssetUrlPermission,
  getUploadPositionPreviewUrlPermission,
  importPresetsFromJsonPermission,
  importPriceDataPermission,
  importUserOptionsFromJsonPermission,
  processModelsArchivePermission,
  renameUserOptionPermission,
  saveAsPresetPermission,
  setProductSettingsPermission,
  triggerImportOrdersPermission,
  uploadPresetsPermission,
  uploadPriceDataPermission,
  uploadUserOptionsPermission,
} from "./provisionReducerPermissions";

const allEntities = [
  Entities.AdditionalArticle,
  Entities.UserOption,
  Entities.Order,
  Entities.Position,
  DefaultEntities.Global,
  DefaultEntities.User,
  Entities.ImportProcess,
  Entities.Preset,
  Entities.PriceData,
];

const entitiesWithoutOrdersAndPositions = [
  DefaultEntities.Global,
  DefaultEntities.User,
];

const orderAndPositionEntities = [Entities.Order, Entities.Position];
const presetEntities = [Entities.Preset];
const presetReducerPermissions = [
  clearAllPresetsPermission,
  importPresetsFromJsonPermission,
  saveAsPresetPermission,
  uploadPresetsPermission,
];
const priceDataReducerPermissions = [
  clearAllPriceDataPermission,
  importPriceDataPermission,
  uploadPriceDataPermission,
];
const userOptionReducerPermissions = [
  clearAllUserOptionsPermission,
  importUserOptionsFromJsonPermission,
  processModelsArchivePermission,
  renameUserOptionPermission,
  uploadUserOptionsPermission,
];
const positionReducerPermissions = [
  deletePositionPreviewPermission,
  getUploadPositionPreviewUrlPermission,
  setProductSettingsPermission,
];
const modelAssetReducerPermissions = [
  deleteModelAssetPermission,
  getUploadModelAssetUrlPermission,
];

/** Explicit pricing reads — required for pricing UI (`canAccessPriceData`); entity-level `Global/read` alone must not grant access. */
const globalPricingAtomsRead = [
  ...allowReadingEntities([Entities.PriceData]),
  makePermission({
    entityType: Entities.PriceData,
    atomType: Atoms.PriceDataInfo,
    permission: GenericPermission.Read,
  }),
  makePermission({
    entityType: DefaultEntities.Global,
    atomType: Atoms.PriceParameters,
    permission: GenericPermission.Read,
  }),
];

/**
 * Single source for backend and frontend `permissionsByRoles`
 * (must stay in sync with `getProvisionRolesFromTransaction` role keys).
 */
export const provisionPermissionsByRoles = {
  admin: [
    ...allowCreatingEntities(allEntities),
    ...allowReadingEntities(allEntities),
    ...allowUpdatingEntities(allEntities),
    ...allowDeletingEntities(allEntities),
    ...globalPricingAtomsRead,
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["isAdmin", "state"],
      GenericPermission.Update,
    ),
    ...grantFieldPermission(
      Entities.Position,
      Atoms.PositionInfo,
      ["createdAt", "source"],
      GenericPermission.Create,
    ),
    CustomPermissions.UpdateProtectedUserFields,
    CustomPermissions.ManageTaggedPresets,
    inviteUserPermission,
    requestEmailChangePermission,
    sendPasswordResetEmailPermission,
    resendInviteCodePermission,
    triggerImportOrdersPermission,
    afsImportApiPermission,
    deleteAllPositionPreviewsPermission,
    ...modelAssetReducerPermissions,
    ...positionReducerPermissions,
    ...presetReducerPermissions,
    ...priceDataReducerPermissions,
    ...userOptionReducerPermissions,
  ],
  user: [
    ...allowReadingEntities(entitiesWithoutOrdersAndPositions),
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["firstName", "lastName", "displayName", "imageUrl", "email"],
      GenericPermission.Update,
    ),
  ],
  usermanager: [
    ...allowCreatingEntities([DefaultEntities.User]),
    ...allowReadingEntities([DefaultEntities.User, DefaultEntities.Global]),
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["firstName", "lastName", "displayName", "imageUrl", "email", "state"],
      GenericPermission.Update,
    ),
    inviteUserPermission,
    requestEmailChangePermission,
    sendPasswordResetEmailPermission,
    resendInviteCodePermission,
  ],
  pricecalculator: [
    ...globalPricingAtomsRead,
    ...allowCreatingEntities([Entities.PriceData]),
    ...allowUpdatingEntities([Entities.PriceData]),
    ...allowDeletingEntities([Entities.PriceData]),
    ...allowUpdatingAtoms(DefaultEntities.Global, Atoms.PriceParameters),
    ...priceDataReducerPermissions,
  ],
  designer: [
    ...allowCreatingEntities(orderAndPositionEntities),
    ...allowReadingEntities(orderAndPositionEntities),
    ...allowUpdatingEntities(orderAndPositionEntities),
    ...allowDeletingEntities(orderAndPositionEntities),
    ...grantFieldPermission(
      Entities.Position,
      Atoms.PositionInfo,
      ["createdAt", "source"],
      GenericPermission.Create,
    ),
    ...allowCreatingEntities(presetEntities),
    ...allowReadingEntities(presetEntities),
    ...allowUpdatingEntities(presetEntities),
    ...allowDeletingEntities(presetEntities),
    ...allowCreatingEntities([Entities.UserOption]),
    ...allowReadingEntities([Entities.UserOption]),
    ...allowUpdatingEntities([Entities.UserOption]),
    ...allowDeletingEntities([Entities.UserOption]),
    ...allowCreatingEntities([Entities.AdditionalArticle]),
    ...allowReadingEntities([Entities.AdditionalArticle]),
    ...allowUpdatingEntities([Entities.AdditionalArticle]),
    ...allowDeletingEntities([Entities.AdditionalArticle]),
    ...allowReadingEntities([Entities.ImportProcess]),
    triggerImportOrdersPermission,
    afsImportApiPermission,
    ...modelAssetReducerPermissions,
    ...positionReducerPermissions,
    ...presetReducerPermissions,
    ...userOptionReducerPermissions,
  ],
  sunlabimporttemplates: [
    ...allowCreatingEntities(presetEntities),
    ...allowReadingEntities(presetEntities),
    ...allowUpdatingEntities(presetEntities),
    ...allowDeletingEntities(presetEntities),
    CustomPermissions.ManageTaggedPresets,
    ...presetReducerPermissions,
  ],
  ordermanager: [
    ...allowReadingEntities([Entities.ImportProcess]),
    triggerImportOrdersPermission,
    afsImportApiPermission,
  ],
} as PermissionsByRole<string, ValidProjectSpecificPermissions>;
